import Container from "@material-ui/core/Container";
import { makeStyles } from "@material-ui/core/styles";
import { RefObject } from "react";
import { Link } from "react-router-dom";
import { changeAppState } from "../../../../../../common/appState";
import { useScreenSize } from "../../../../../../common/hooks/useScreenSize";
import { useUser } from "../../../../../../common/hooks/useUser";
import { FullScreenShurikenProgress } from "../../../../../shared/Animations/ShurikenProgress";
import { DarkButton } from "../../../../../shared/Button/DarkButton";
import { GoogleAd } from "../../../../../shared/GoogleAd";
import { User } from "../../../../../shared/User/types";
import { useOpenState, useStyles } from "../../SignUp/SignUp";
import { SignInPanelState } from "../../types";
import { AvatarField } from "../Fields/AvatarField";
import { BioField } from "../Fields/BioField";
import { BioElementToEmphasize } from "../Fields/BioField/types";
import { HomeCountryField } from "../Fields/HomeCountryField";
import { UsernameField } from "../Fields/UsernameField";
import { BonusCard } from "../components/OpenableCards/BonusCard/BonusCard";
import { FriendsCard } from "../components/OpenableCards/FriendsCard/FriendsCard";
import { ItemsCard } from "../components/OpenableCards/ItemsCard/ItemsCard";
import { MessagesCard } from "../components/OpenableCards/MessagesCard/MessagesCard";
import { ProgressCard } from "../components/OpenableCards/ProgressCard";
import { QuestsCard } from "../components/OpenableCards/QuestsCard/QuestsCard";
import { RankingAroundMe } from "../components/OpenableCards/RankingAroundMe/RankingAroundMe";
import { XpProgressArea } from "../components/XpProgressBar";
import "../style.css";
import { XpBoostTimer } from "./components/XpBoostTimer/XpBoostTimer";
import { logout } from "./logout";
import { InitialView } from "./types";
import { useScrollToMyPageItem } from "./useScrollToMyPageItem";

export function MyPageTop({
    chosen,
    panelClosed,
    panelState,
    scrollableContainerRef,
}: {
    chosen: boolean;
    panelClosed: boolean;
    panelState: SignInPanelState;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const { screenWidth } = useScreenSize();
    const { partiallyOpened, containerC } = useOpenState(
        chosen,
        panelClosed,
        screenWidth > 600 ? 25 : 10
    );
    if (!partiallyOpened) {
        return null;
    }

    return (
        <Container component="div" className={containerC.container}>
            <Content
                panelState={panelState}
                scrollableContainerRef={scrollableContainerRef}
            />
        </Container>
    );
}

function Content({
    panelState,
    scrollableContainerRef,
}: {
    panelState: SignInPanelState;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const classes = useStyles();
    const myPageClasses = useMyPageTopStyles();
    const { screenHeight, screenWidth } = useScreenSize();
    const { user } = useUser();

    if (!user) {
        return (
            <FullScreenShurikenProgress
                style={{ height: screenHeight - 106 }}
            />
        );
    }

    const { initialView, itemKeyToFocus, elementToEmphasize } =
        panelState.type === "myPageTop"
            ? {
                  initialView: panelState.initialView,
                  itemKeyToFocus:
                      panelState.initialView === "MyPageItemsCard"
                          ? panelState.itemKeyToFocus
                          : undefined,
                  elementToEmphasize:
                      panelState.initialView === "MyPageBio"
                          ? panelState.elementToEmphasize
                          : undefined,
              }
            : {
                  initialView: undefined,
                  itemKeyToFocus: undefined,
                  elementToEmphasize: undefined,
              };

    return (
        <div className={classes.paper}>
            <ProfileCard
                user={user}
                screenWidth={screenWidth}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
                elementToEmphasize={elementToEmphasize}
            />

            <GoogleAd className={myPageClasses.ad1} />

            <ItemsCard
                user={user}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
                itemKeyToFocus={itemKeyToFocus}
            />
            <MessagesCard
                user={user}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />
            <FriendsCard
                user={user}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />
            <QuestsCard
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />
            <RankingAroundMe
                user={user}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />
            <ProgressCard
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />
            <BonusCard
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />

            <GoogleAd className={myPageClasses.ad2} />

            {user.sul < 4 && (
                <Link to="/" className={myPageClasses.logoutLink}>
                    <DarkButton onClick={onLogout} fullWidth>
                        Logout
                    </DarkButton>
                </Link>
            )}
        </div>
    );
}
const useMyPageTopStyles = makeStyles({
    ad1: {
        width: "100%",
        marginTop: 10,
        marginBottom: 25,
    },
    ad2: {
        width: "100%",
        marginTop: 30,
    },
    logoutLink: { marginTop: 30, width: "100%" },
});

function ProfileCard({
    user,
    screenWidth,
    initialView,
    scrollableContainerRef,
    elementToEmphasize,
}: {
    user: User;
    screenWidth: number;
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
    elementToEmphasize?: BioElementToEmphasize;
}) {
    const c = useStatusCardStyles();

    const isProfileEditable = user.sul < 3;

    return (
        <div className={c.card}>
            <AvatarField user={user} isProfileEditable={isProfileEditable} />

            <UsernameField user={user} isProfileEditable={isProfileEditable} />

            {isProfileEditable && (
                <HomeCountryField user={user} initialView={initialView} />
            )}

            <LevelAndXp
                user={user}
                screenWidth={screenWidth}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
            />

            <BioField
                user={user}
                isProfileEditable={isProfileEditable}
                initialView={initialView}
                scrollableContainerRef={scrollableContainerRef}
                elementToEmphasize={elementToEmphasize}
            />
        </div>
    );
}
const useStatusCardStyles = makeStyles({
    card: {
        width: "100%",
        fontSize: "large",
        margin: "0 0 5px",
        padding: "0 20px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
});

function LevelAndXp({
    user,
    screenWidth,
    initialView,
    scrollableContainerRef,
}: {
    user: User;
    screenWidth: number;
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const { scrollTargetRef } = useScrollToMyPageItem<HTMLTableElement>({
        scrollableContainerRef,
        initialView,
        itemName: "MyPageLevelAndXp",
    });

    return (
        <>
            <table className="progressTable" ref={scrollTargetRef}>
                <tbody>
                    <tr>
                        <td className="bold x-large">{"Japanese Level:"}</td>
                        <td className="alignRight total">{user.level}</td>
                    </tr>
                </tbody>
            </table>
            <XpProgressArea />
            <XpBoostTimer screenWidth={screenWidth} />
        </>
    );
}

function onLogout(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!confirm("Do you really want to logout?")) {
        // ログアウト中止時はトップページに画面遷移しない
        // （ちなみに画面遷移の目的は、パスワードリセットのURLで開いているときにログアウトができなくなることを防ぐため）
        ev.stopPropagation();
        ev.preventDefault();
        return;
    }

    changeAppState("signInPanelState", { type: "signIn" });

    logout();
}
